import { api } from "@/lib/api";
import type {
  AddonCheckoutResponse,
  AddonPurchaseRequest,
  BillingAddonCatalogResponse,
  BillingCheckoutRequest,
  BillingPurchasablePriceListResponse,
  BillingMessageResponse,
  BillingSummary,
  BillingUrlResponse,
  CancelSubscriptionResponse,
  InvoiceListResponse,
  PaymentMethodResponse,
  PendingPlanChangeEnvelope,
  PlanChangeCancelResponse,
  PlanChangeQuoteResponse,
  PlanChangeRequest,
  PlanUpgradeResponse,
  StorageAddonListResponse,
  StoragePurchaseResponse,
  StorageQuoteResponse,
  StorageRemovalResponse,
} from "@/types/billing";

export const billingService = {
  getPrices: async (): Promise<BillingPurchasablePriceListResponse> => {
    const response = await api.get<BillingPurchasablePriceListResponse>(
      "/api/v1/billing/prices"
    );
    return response.data;
  },

  getAddonCatalog: async (): Promise<BillingAddonCatalogResponse> => {
    const response = await api.get<BillingAddonCatalogResponse>(
      "/api/v1/billing/addons/catalog"
    );
    return response.data;
  },

  createCreditAddonCheckout: async (
    payload: AddonPurchaseRequest
  ): Promise<AddonCheckoutResponse> => {
    const response = await api.post<AddonCheckoutResponse>(
      "/api/v1/billing/addons/credits/checkout",
      payload
    );
    return response.data;
  },

  getStorageAddonQuote: async (
    priceId: string,
    quantity = 1
  ): Promise<StorageQuoteResponse> => {
    const response = await api.get<StorageQuoteResponse>(
      "/api/v1/billing/addons/storage/quote",
      { params: { price_id: priceId, quantity } }
    );
    return response.data;
  },

  purchaseStorageAddon: async (
    payload: AddonPurchaseRequest
  ): Promise<StoragePurchaseResponse> => {
    const response = await api.post<StoragePurchaseResponse>(
      "/api/v1/billing/addons/storage/purchase",
      payload
    );
    return response.data;
  },

  getStorageAddons: async (): Promise<StorageAddonListResponse> => {
    const response = await api.get<StorageAddonListResponse>(
      "/api/v1/billing/addons/storage"
    );
    return response.data;
  },

  removeAllStorageAddons: async (): Promise<StorageRemovalResponse> => {
    const response = await api.post<StorageRemovalResponse>(
      "/api/v1/billing/addons/storage/remove-all"
    );
    return response.data;
  },

  removeStorageAddon: async (
    subscriptionItemId: string
  ): Promise<StorageRemovalResponse> => {
    const response = await api.delete<StorageRemovalResponse>(
      `/api/v1/billing/addons/storage/${encodeURIComponent(subscriptionItemId)}`
    );
    return response.data;
  },

  getSummary: async (): Promise<BillingSummary> => {
    const response = await api.get<BillingSummary>("/api/v1/billing/summary");
    return response.data;
  },

  createCheckout: async (
    payload: BillingCheckoutRequest
  ): Promise<BillingUrlResponse> => {
    const response = await api.post<BillingUrlResponse>(
      "/api/v1/billing/checkout",
      payload
    );
    return response.data;
  },

  getSuccessMessage: async (
    queryString = ""
  ): Promise<BillingMessageResponse> => {
    const response = await api.get<BillingMessageResponse>(
      `/api/v1/billing/success${queryString}`
    );
    return response.data;
  },

  getCancelMessage: async (): Promise<BillingMessageResponse> => {
    const response = await api.get<BillingMessageResponse>(
      "/api/v1/billing/cancel"
    );
    return response.data;
  },

  getInvoices: async (): Promise<InvoiceListResponse> => {
    const response = await api.get<InvoiceListResponse>(
      "/api/v1/billing/invoices"
    );
    return response.data;
  },

  getPaymentMethod: async (): Promise<PaymentMethodResponse> => {
    const response = await api.get<PaymentMethodResponse>(
      "/api/v1/billing/payment-method"
    );
    return response.data;
  },

  updatePaymentMethod: async (): Promise<BillingUrlResponse> => {
    const response = await api.post<BillingUrlResponse>(
      "/api/v1/billing/payment-method/update"
    );
    return response.data;
  },

  openPortal: async (): Promise<BillingUrlResponse> => {
    const response = await api.post<BillingUrlResponse>("/api/v1/billing/portal");
    return response.data;
  },

  cancelSubscription: async (): Promise<CancelSubscriptionResponse> => {
    const response = await api.post<CancelSubscriptionResponse>(
      "/api/v1/billing/subscription/cancel"
    );
    return response.data;
  },

  getPlanChangeQuote: async (priceId: string): Promise<PlanChangeQuoteResponse> => {
    const response = await api.get<PlanChangeQuoteResponse>(
      "/api/v1/billing/plan-change/quote",
      { params: { price_id: priceId } }
    );
    return response.data;
  },

  changePlan: async (
    payload: PlanChangeRequest
  ): Promise<PlanUpgradeResponse | PendingPlanChangeEnvelope> => {
    const response = await api.post<PlanUpgradeResponse | PendingPlanChangeEnvelope>(
      "/api/v1/billing/plan-change",
      payload
    );
    return response.data;
  },

  getPendingPlanChange: async (): Promise<PendingPlanChangeEnvelope> => {
    const response = await api.get<PendingPlanChangeEnvelope>(
      "/api/v1/billing/plan-change/pending"
    );
    return response.data;
  },

  cancelPendingPlanChange: async (): Promise<PlanChangeCancelResponse> => {
    const response = await api.delete<PlanChangeCancelResponse>(
      "/api/v1/billing/plan-change/pending"
    );
    return response.data;
  },
};
